// Template legal documents for Northwind Markets.
// NOTE: These are professional templates — have them reviewed by a qualified
// legal advisor before relying on them in your jurisdiction.

const EFFECTIVE = "Last updated: June 2026";

export const LEGAL_DOCS = {
  terms: {
    title: "Terms of Service",
    intro:
      "These Terms of Service govern your access to and use of the Northwind Markets website, trading platform, and related services (collectively, the “Services”). By creating an account or using the Services, you agree to be bound by these Terms.",
    sections: [
      {
        heading: "1. Eligibility",
        body: [
          "You must be at least 18 years old and legally capable of entering into binding contracts to use the Services.",
          "The Services are not available to residents of jurisdictions where the provision of such services would be contrary to local law or regulation.",
        ],
      },
      {
        heading: "2. Account Registration",
        body: [
          "You agree to provide accurate, current, and complete information during registration and to keep it updated.",
          "You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.",
          "We may require identity verification (KYC) before enabling deposits, trading, or withdrawals.",
        ],
      },
      {
        heading: "3. Deposits, Withdrawals & Bonuses",
        body: [
          "Deposits may be made via the funding methods displayed in your dashboard. Funds are credited after confirmation.",
          "Promotional bonuses (including any first-deposit bonus) are subject to the specific conditions stated at the time of the offer.",
          "Withdrawals are processed to the same or a verified destination and may be subject to applicable lock-in periods, such as the copy-trading lock described in your account.",
        ],
      },
      {
        heading: "4. Trading & Copy Trading",
        body: [
          "All trading and copy-trading activity is undertaken at your own risk. Past performance of any strategy or trader is not indicative of future results.",
          "We do not provide personalised investment advice. Any market information is general in nature.",
        ],
      },
      {
        heading: "5. Affiliate Program",
        body: [
          "Affiliate commissions are paid in accordance with the rates published in your affiliate dashboard and may be amended with notice.",
          "Self-referrals, fraudulent activity, or abuse of the program may result in forfeiture of commissions and account suspension.",
        ],
      },
      {
        heading: "6. Prohibited Conduct",
        body: [
          "You agree not to use the Services for unlawful purposes, to manipulate markets, to launder money, or to circumvent security measures.",
        ],
      },
      {
        heading: "7. Limitation of Liability",
        body: [
          "To the fullest extent permitted by law, Northwind Markets shall not be liable for any indirect, incidental, or consequential losses arising from your use of the Services.",
        ],
      },
      {
        heading: "8. Changes to These Terms",
        body: [
          "We may update these Terms from time to time. Continued use of the Services after changes take effect constitutes acceptance of the revised Terms.",
        ],
      },
      {
        heading: "9. Contact",
        body: ["Questions about these Terms can be sent to support@northwindmarkets.com."],
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains how Northwind Markets collects, uses, and protects your personal information when you use our Services.",
    sections: [
      {
        heading: "1. Information We Collect",
        body: [
          "Account data: name, email, phone number, and the source through which you found us.",
          "Verification data: identity documents or national identification numbers submitted for KYC.",
          "Financial data: deposit, withdrawal, trading, and affiliate activity.",
          "Technical data: device, browser, IP address, and usage analytics.",
        ],
      },
      {
        heading: "2. How We Use Your Information",
        body: [
          "To create and manage your account and process transactions.",
          "To verify your identity and comply with legal and regulatory obligations.",
          "To operate the affiliate program and calculate commissions.",
          "To send transactional emails (welcome, deposit/withdrawal confirmations, KYC updates) and respond to support requests.",
          "To improve and secure our Services.",
        ],
      },
      {
        heading: "3. Sharing of Information",
        body: [
          "We share data with trusted service providers (e.g., email delivery, live chat, analytics) strictly to operate the Services, and with authorities where required by law.",
          "We do not sell your personal information.",
        ],
      },
      {
        heading: "4. Data Retention & Security",
        body: [
          "We retain personal data for as long as necessary to provide the Services and meet legal obligations.",
          "We apply technical and organisational measures to protect your data, though no method of transmission is completely secure.",
        ],
      },
      {
        heading: "5. Your Rights",
        body: [
          "Depending on your jurisdiction, you may have rights to access, correct, delete, or restrict the processing of your personal data. Contact us to exercise these rights.",
        ],
      },
      {
        heading: "6. Cookies",
        body: [
          "We use cookies and similar technologies for authentication, preferences, referral tracking, and analytics.",
        ],
      },
      {
        heading: "7. Contact",
        body: ["For privacy enquiries, email support@northwindmarkets.com."],
      },
    ],
  },

  risk: {
    title: "Risk Disclosure",
    intro:
      "Trading leveraged products such as forex and CFDs carries a high level of risk and may not be suitable for all investors. Please read this disclosure carefully.",
    sections: [
      {
        heading: "1. High Risk of Loss",
        body: [
          "Leveraged trading can result in losses that exceed your initial deposit. You should not trade with money you cannot afford to lose.",
        ],
      },
      {
        heading: "2. Leverage",
        body: [
          "Leverage magnifies both gains and losses. A small market movement can have a proportionally larger impact on your funds.",
        ],
      },
      {
        heading: "3. No Guaranteed Returns",
        body: [
          "No representation is made that any account will or is likely to achieve profits. Advertised or historical returns from traders or strategies are not guarantees of future performance.",
        ],
      },
      {
        heading: "4. Copy Trading Risk",
        body: [
          "Copying another trader does not reduce market risk. The strategy you copy may incur losses, and a 30-day lock-in period may apply to allocated funds.",
        ],
      },
      {
        heading: "5. Market & Liquidity Risk",
        body: [
          "Prices can move rapidly due to economic events, and at times of low liquidity orders may be executed at prices different from those requested.",
        ],
      },
      {
        heading: "6. Independent Advice",
        body: [
          "The information provided is general and does not constitute personal financial advice. Seek independent advice if you are unsure whether trading is appropriate for you.",
        ],
      },
      {
        heading: "7. Contact",
        body: ["If you have questions about these risks, contact support@northwindmarkets.com."],
      },
    ],
  },
};

export const LEGAL_EFFECTIVE = EFFECTIVE;
